import React from 'react';
import { Box, Typography } from '@mui/material';
import PercentInputField, { clampPctInput } from './PercentInputField';
import { EARNED_PCT_INLINE_GROUP_PX } from './pctLayout';
import { calculateForecastAndVariance } from './forecastMath';

const formatMetricHours = (value) => {
  if (value == null || !Number.isFinite(value)) return '—';
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};

const metricLabelSx = {
  fontSize: '10px',
  color: 'text.secondary',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  lineHeight: 1.2,
  // Labels may wrap in narrow (prefab) columns; the metric grid bottom-aligns
  // blocks so the hours values below them stay on one line.
};

const metricValueSx = {
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: 1.2,
  mt: 0.35,
  whiteSpace: 'nowrap',
};

const MetricBlock = ({ label, value, valueSx = {} }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={metricLabelSx}>{label}</Typography>
    <Typography sx={{ ...metricValueSx, ...valueSx }}>
      {formatMetricHours(value)}
      {value != null && Number.isFinite(value) && (
        <Box
          component="span"
          sx={{
            fontSize: '0.62em',
            color: 'text.secondary',
            fontWeight: 500,
            ml: 0.5,
          }}
        >
          hrs
        </Box>
      )}
    </Typography>
  </Box>
);

/**
 * Earned % (same input as budget allocation when editable) → earned hrs.
 * Read-only: static "N %" text (e.g. shop complete — chip lives on Shop card header).
 */
const EarnedRow = ({
  earnedPct,
  budgetHrs,
  actualHrs,
  onPctChange,
  readOnly,
  disabled = false,
}) => {
  const pct = clampPctInput(earnedPct);
  const earnedHrs = budgetHrs * (pct / 100) || 0;
  const { forecastHrs, leftToCompleteHrs, budgetVarianceHrs } =
    calculateForecastAndVariance(budgetHrs, actualHrs, pct);

  const handlePctChange = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      onPctChange(0);
      return;
    }
    onPctChange(clampPctInput(raw));
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `${EARNED_PCT_INLINE_GROUP_PX}px minmax(0, 1fr)`,
        alignItems: 'center',
        columnGap: 1.25,
        rowGap: 1,
        width: '100%',
      }}
    >
      <Box
        sx={{
          width: EARNED_PCT_INLINE_GROUP_PX,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}
      >
        {readOnly ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 0.25,
            }}
          >
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: 700,
                lineHeight: 1.2,
                textAlign: 'right',
                minWidth: '3.8ch',
                whiteSpace: 'nowrap',
              }}
            >
              {String(pct)}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: '20px',
                fontWeight: 700,
                lineHeight: 1.2,
                color: 'text.secondary',
              }}
            >
              %
            </Typography>
          </Box>
        ) : (
          <PercentInputField
            value={pct}
            onChange={handlePctChange}
            disabled={disabled}
            inputProps={{ 'aria-label': 'Percent complete' }}
          />
        )}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(86px, 1fr))',
            alignItems: 'end',
            gap: 1,
          }}
        >
          <MetricBlock label="Earned Hrs" value={earnedHrs} />
          <MetricBlock label="Forecast Hrs" value={forecastHrs} />
          <MetricBlock
            label="Left to Complete"
            value={leftToCompleteHrs}
            valueSx={{
              color:
                leftToCompleteHrs != null && leftToCompleteHrs < 0
                  ? 'warning.main'
                  : 'inherit',
            }}
          />
          <MetricBlock
            label="Budget Variance"
            value={budgetVarianceHrs}
            valueSx={{
              color:
                budgetVarianceHrs != null && budgetVarianceHrs < 0
                  ? 'error.main'
                  : 'inherit',
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default EarnedRow;
